import { Dropdown, Form, Input, Select } from '@duxton-web/ui';
import _ from 'lodash';
import React from 'react';

import { SMART_CAMPAIGN_FORM_KEYS } from 'omega-rtc/constants/ucd';
import { getFormattedCVPKey } from 'omega-rtc/utils/smart-campaign-utils';
import {
  isTaiwanCampaignCreationAllowed,
  TAIWAN_CAMPAIGN_CREATION_RESTRICTED_MESSAGE,
} from 'omega-rtc/utils/taiwan-campaign-operation';

const getDisallowedCountryIDs = (): number[] => _.get(window, 'Grab.config.PORTAL_CONFIG.holos.createCampaignDisallowedCountryIDs', []);

type ProjectView = {
  projectID: string;
  project: {
    name: string;
    countryID: number;
  };
};

type ProjectSuggestion = {
  projectID: string;
  project: {
    name: string;
  };
};

type VerticalOption = {
  key: string;
  label: string;
  children?: any[];
};

type Props = {
  onProjectSearch: (value: string) => void;
  handleProjectSelect: (value: string) => void;
  isProjectAndVerticalSelectorDisabled: boolean;
  projectSuggestions: ProjectSuggestion[];
  handleValueChangeInVerticalSelector: (info: any) => void;
  verticalOptions: VerticalOption[];
  watchVertical: string;
  cvpValue: string;
  formattedVerticalSelectorLabel: string;
  projectView: ProjectView | null;
  userEmail?: string;
  isEditMode?: boolean;
};

const SmartCampaignMetaDataContext = ({
  onProjectSearch,
  handleProjectSelect,
  isProjectAndVerticalSelectorDisabled,
  projectSuggestions,
  handleValueChangeInVerticalSelector,
  verticalOptions,
  watchVertical,
  cvpValue,
  formattedVerticalSelectorLabel,
  projectView,
  userEmail,
  isEditMode,
}: Props) => {
  return (
    <React.Fragment>
      <Form.Item
        label="Campaign name"
        required
        name={SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME}
        rules={[{ message: 'Please input campaign name', required: true }]}
      >
        <Input placeholder="Enter campaign name" />
      </Form.Item>
      <Form.Item
        label="Project"
        required
        name={SMART_CAMPAIGN_FORM_KEYS.PROJECT}
        rules={[
          { message: 'Please select a project', required: true },
          {
            validator: (_, value) => {
              if (
                value
                && projectView
                && projectView.project
                && !isEditMode
              ) {
                if (getDisallowedCountryIDs().includes(projectView.project.countryID)) {
                  return Promise.reject(new Error('Creating campaign is not allowed for this country'));
                }
                if (!isTaiwanCampaignCreationAllowed(
                  projectView.project.countryID,
                  userEmail,
                )) {
                  return Promise.reject(
                    new Error(TAIWAN_CAMPAIGN_CREATION_RESTRICTED_MESSAGE),
                  );
                }
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Select
          autoClearSearchValue={false}
          filterOption={false}
          showSearch
          placeholder="Select project"
          onSearch={onProjectSearch}
          onSelect={handleProjectSelect}
          disabled={isProjectAndVerticalSelectorDisabled}
          options={projectSuggestions.map((projectSuggestion) => ({
            value: projectSuggestion.projectID,
            label: projectSuggestion.project.name,
          }))}
        />
      </Form.Item>
      <Form.Item
        label="Vertical"
        required
        name={SMART_CAMPAIGN_FORM_KEYS.VERTICAL}
        rules={[{ message: 'Please select a vertical', required: true }]}
      >
        <Dropdown
          trigger={['click']}
          disabled={isProjectAndVerticalSelectorDisabled}
          menu={{
            onClick: handleValueChangeInVerticalSelector,
            items: verticalOptions,
            selectedKeys: [
              getFormattedCVPKey({ vertical: watchVertical, cvp: cvpValue }),
              watchVertical,
            ].filter((v) => v),
            selectable: true,
          }}
        >
          <Select
            open={false}
            placeholder="Select Vertical"
            value={formattedVerticalSelectorLabel}
          />
        </Dropdown>
      </Form.Item>
    </React.Fragment>
  );
};

export default SmartCampaignMetaDataContext;
